import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  ClipboardList,
  CreditCard,
  Gift,
  History,
  House,
  LogIn,
  Ticket,
  UserPlus,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@/shared/api/client';
import { useConfirm } from '@/shared/components/confirm';
import { RoundTabs, type RoundTabItem } from '@/shared/components/round-tabs';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { fetchPublicInvitePassbook, type PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import { PublicInviteInactive } from '@/features/public-invite/ui/public-invite-inactive';
import { PublicInviteLayout } from '@/features/public-invite/ui/public-invite-layout';
import {
  onParticipantSessionExpired,
  participantApi,
  type ParticipantMe,
  type ParticipantProfile,
} from '../api/participant-api';
import { clearParticipant, prefetchParticipant } from '../api/use-participant';
import { hasRulesOrTerms, participantFormPlan } from '../model/form-plan';
import { formatBalance, programPassbook } from '../model/history';
import { ParticipantLogin } from './participant-login';
import { ParticipantRegister } from './participant-register';
import { ParticipantSummary } from './participant-summary';
import { RulesAndTerms } from './rules-and-terms';
import { CardTab } from './tabs/card-tab';
import { CouponsTab } from './tabs/coupons-tab';
import { HistoryTab } from './tabs/history-tab';
import { HomeTab, type HomeDestination } from './tabs/home-tab';
import { MgmTab } from './tabs/mgm-tab';
import { ProfileTab } from './tabs/profile-tab';
import { SurveyTab } from './tabs/survey-tab';

/** Visitante: sem bolinhas. Entra pelo Entrar; Cadastro e Informações são links a partir dele. */
type GuestView = 'entrar' | 'cadastro' | 'informacoes';
/** Logado: Início + seções (bolinhas). `regras` não tem bolinha: abre pelo Início. */
type MemberView = 'inicio' | 'dados' | 'historico' | 'cartao' | 'cupons' | 'indique' | 'pesquisa' | 'regras';

const GUEST_VIEWS: GuestView[] = ['entrar', 'cadastro', 'informacoes'];

const MEMBER_TABS: Record<Exclude<MemberView, 'regras'>, RoundTabItem<MemberView>> = {
  inicio: { id: 'inicio', label: 'Início', icon: House },
  dados: { id: 'dados', label: 'Meus dados', icon: UserRound },
  historico: { id: 'historico', label: 'Histórico', icon: History },
  cartao: { id: 'cartao', label: 'Meu cartão', icon: CreditCard },
  cupons: { id: 'cupons', label: 'Cupons', icon: Ticket },
  indique: { id: 'indique', label: 'Indique', icon: Gift },
  pesquisa: { id: 'pesquisa', label: 'Pesquisa', icon: ClipboardList },
};

/** `?cartao=<serial>`: o QR da aba Meu cartão (computador) abre direto no celular. */
const CARD_SERIAL = /^[A-Za-z0-9-]{4,64}$/;

type MeState =
  | { kind: 'loading' }
  | { kind: 'guest' }
  | { kind: 'member'; me: ParticipantMe }
  | { kind: 'unavailable' }
  | { kind: 'error' };

function PageSkeleton() {
  return (
    <div className="container mx-auto max-w-2xl flex-1 px-4 py-10">
      <div className="space-y-6">
        <Skeleton className="mx-auto h-16 w-48" />
        <Skeleton className="mx-auto h-72 w-full max-w-xl rounded-2xl" />
      </div>
    </div>
  );
}

/**
 * Conteúdo da aba. Só animação de ENTRADA, curta e sem sumir: a anterior sai
 * na hora (sem esperar fade de saída) e os dados já vêm do cache, então a
 * troca não pisca.
 */
function TabPanel({ tabKey, children }: { tabKey: string; children: ReactNode }) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div>{children}</div>;
  return (
    <motion.div
      key={tabKey}
      initial={{ opacity: 0.6, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

/** "← voltar" das telas de visitante que saem do Entrar. */
function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="mx-auto mb-4 w-full max-w-xl">
      <Button variant="ghost" size="sm" className="-ml-2" onClick={onClick}>
        <ArrowLeft className="size-4" />
        {label}
      </Button>
    </div>
  );
}

/**
 * Área do participante (`/clube/:idPassbook`), só web. Visitante cai direto no
 * Entrar (CPF + celular → código no WhatsApp), com Cadastro e Regras a um
 * toque. Logado, abre no Início (cartão, atalhos, últimas movimentações) com
 * as bolinhas das seções; os logos (loja e Fixe) sempre voltam ao Início.
 * Mesmo layout do convite. Vale para todas as lojas.
 */
export function ParticipantPage() {
  const { idPassbook = '' } = useParams<{ idPassbook: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const campanha = searchParams.get('campanha') ?? undefined;
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [passbook, setPassbook] = useState<PublicInvitePassbook | null>(null);
  const [passbookMissing, setPassbookMissing] = useState(false);
  const [meState, setMeState] = useState<MeState>({ kind: 'loading' });
  const [loggingOut, setLoggingOut] = useState(false);
  const profileDirty = useRef(false);
  const isMember = useRef(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!idPassbook) return;
    let cancelled = false;
    fetchPublicInvitePassbook(idPassbook)
      .then((data) => !cancelled && setPassbook(data))
      .catch(() => !cancelled && setPassbookMissing(true));
    return () => {
      cancelled = true;
    };
  }, [idPassbook]);

  const enterAccount = useCallback(
    (me: ParticipantMe) => {
      setMeState({ kind: 'member', me });
      prefetchParticipant(queryClient, idPassbook, me);
    },
    [idPassbook, queryClient],
  );

  const loadMe = useCallback(() => {
    if (!idPassbook) return;
    setMeState({ kind: 'loading' });
    participantApi
      .me(idPassbook)
      .then(enterAccount)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) setMeState({ kind: 'guest' });
        else if (err instanceof ApiError && err.status === 404) setMeState({ kind: 'unavailable' });
        else setMeState({ kind: 'error' });
      });
  }, [idPassbook, enterAccount]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    isMember.current = meState.kind === 'member';
  }, [meState.kind]);

  const me = meState.kind === 'member' ? meState.me : null;
  const formPlan = useMemo(() => (passbook ? participantFormPlan(passbook) : null), [passbook]);
  const showRules = passbook ? hasRulesOrTerms(passbook) : false;

  const memberTabs = useMemo(() => {
    if (!me) return [];
    const list: RoundTabItem<MemberView>[] = [MEMBER_TABS.inicio, MEMBER_TABS.dados, MEMBER_TABS.historico];
    if (me.serialNumber) list.push(MEMBER_TABS.cartao);
    if (me.features.coupons) list.push(MEMBER_TABS.cupons);
    if (me.features.mgm) list.push(MEMBER_TABS.indique);
    if (me.features.surveyId) list.push(MEMBER_TABS.pesquisa);
    return list;
  }, [me]);

  const requested = searchParams.get('aba') ?? '';
  const cardSerialParam = searchParams.get('cartao') ?? '';
  const cardSerial = CARD_SERIAL.test(cardSerialParam) ? cardSerialParam : '';
  const guestView: GuestView = (GUEST_VIEWS as string[]).includes(requested)
    ? (requested as GuestView)
    : 'entrar';
  const memberView: MemberView =
    requested === 'regras' && showRules
      ? 'regras'
      : (memberTabs.find((t) => t.id === requested)?.id ?? 'inicio');

  const goTo = useCallback(
    (view: string, replace = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('aba', view);
          next.delete('cartao');
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  /** Trocou de tela lá embaixo (histórico longo): volta ao topo do conteúdo para a nova aparecer do começo. */
  const revealTop = useCallback(() => {
    requestAnimationFrame(() => {
      const el = topRef.current;
      if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const showGuest = useCallback(
    (view: GuestView) => {
      goTo(view);
      revealTop();
    },
    [goTo, revealTop],
  );

  const leaveAccount = useCallback(() => {
    isMember.current = false;
    profileDirty.current = false;
    clearParticipant(queryClient, idPassbook);
    setMeState({ kind: 'guest' });
    goTo('entrar', true);
  }, [goTo, idPassbook, queryClient]);

  useEffect(
    () =>
      onParticipantSessionExpired(() => {
        if (!isMember.current) return;
        toast.info('Sua sessão terminou. Entre de novo para continuar.');
        leaveAccount();
      }),
    [leaveAccount],
  );

  /** Saindo de Meus dados com edição pendente: pergunta antes de descartar. */
  const confirmLeaveProfile = useCallback(async () => {
    if (!profileDirty.current) return true;
    const ok = await confirm({
      title: 'Descartar alterações?',
      description: 'Você mudou seus dados e ainda não salvou.',
      confirmLabel: 'Descartar',
      cancelLabel: 'Continuar editando',
      variant: 'destructive',
    });
    if (ok) profileDirty.current = false;
    return ok;
  }, [confirm]);

  const showMember = async (view: MemberView) => {
    if (view === memberView) {
      revealTop();
      return;
    }
    if (memberView === 'dados' && !(await confirmLeaveProfile())) return;
    goTo(view);
    revealTop();
  };

  const onSignedIn = (signed: ParticipantMe) => {
    enterAccount(signed);
    goTo('inicio', true);
    toast.success('Você entrou na sua conta.');
  };

  const onLogout = async () => {
    if (!(await confirmLeaveProfile())) return;
    setLoggingOut(true);
    try {
      await participantApi.logout(idPassbook);
    } catch {
      /* o cookie expira sozinho; a tela sai do mesmo jeito */
    }
    setLoggingOut(false);
    leaveAccount();
  };

  const onProfileSaved = (profile: ParticipantProfile) => {
    setMeState((prev) =>
      prev.kind === 'member'
        ? { kind: 'member', me: { ...prev.me, profile, firstName: profile.name.split(/\s+/)[0] ?? prev.me.firstName } }
        : prev,
    );
  };

  const onProfileDirty = useCallback((dirty: boolean) => {
    profileDirty.current = dirty;
  }, []);

  // Salvar Meus dados mantém o mesmo `me.program`: o histórico não recalcula à toa.
  const meProgram = me?.program;
  const program = useMemo(() => (meProgram ? programPassbook(idPassbook, meProgram) : null), [idPassbook, meProgram]);

  useEffect(() => {
    if (!passbook) return;
    document.title = `${passbook.organizationName || passbook.clientName} · ${me ? 'Minha conta' : 'Programa de fidelidade'}`;
  }, [passbook, me]);

  if (!idPassbook || passbookMissing) {
    return (
      <div className="container mx-auto flex-1 px-4 py-10">
        <PublicInviteInactive title="Página não encontrada" description="Verifique se o link está correto." />
      </div>
    );
  }

  if (!passbook || !formPlan || meState.kind === 'loading') return <PageSkeleton />;

  if (passbook.inactive || meState.kind === 'unavailable') {
    return (
      <PublicInviteLayout passbook={passbook}>
        <PublicInviteInactive
          title="Área indisponível"
          description="A área do cliente desta loja não está disponível no momento."
        />
      </PublicInviteLayout>
    );
  }

  if (meState.kind === 'error') {
    return (
      <PublicInviteLayout passbook={passbook}>
        <Card className="mx-auto flex max-w-md flex-col items-center gap-3 p-8 text-center">
          <p className="font-medium">Não foi possível carregar agora.</p>
          <Button variant="outline" onClick={loadMe}>
            Tentar de novo
          </Button>
        </Card>
      </PublicInviteLayout>
    );
  }

  const accent = { accentColor: passbook.backgroundColor, foregroundColor: passbook.foregroundColor };

  // Veio do QR do computador, sem sessão neste celular: só o cartão, sem pedir login.
  if (!me && cardSerial) {
    return (
      <PublicInviteLayout passbook={passbook} onHomeClick={() => showGuest('entrar')}>
        <CardTab passbook={passbook} serialNumber={cardSerial} {...accent} />
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={() => showGuest('entrar')}>
            <LogIn className="size-4" />
            Entrar na minha conta
          </Button>
        </div>
      </PublicInviteLayout>
    );
  }

  if (!me || !program) {
    return (
      <PublicInviteLayout passbook={passbook} onHomeClick={() => showGuest('entrar')}>
        <div ref={topRef} className="scroll-mt-4" />
        <TabPanel tabKey={guestView}>
          {guestView === 'cadastro' ? (
            <>
              <BackLink label="Já tenho cadastro" onClick={() => showGuest('entrar')} />
              <ParticipantRegister
                passbook={passbook}
                campanha={campanha}
                onGoToLogin={() => showGuest('entrar')}
              />
            </>
          ) : guestView === 'informacoes' ? (
            <>
              <BackLink label="Voltar para entrar" onClick={() => showGuest('entrar')} />
              <RulesAndTerms passbook={passbook} />
            </>
          ) : (
            <>
              {formPlan.identity ? (
                <ParticipantLogin
                  idPassbook={idPassbook}
                  identity={formPlan.identity}
                  identityLabel={formPlan.identityLabel}
                  {...accent}
                  onSignedIn={onSignedIn}
                  onGoToRegister={() => showGuest('cadastro')}
                />
              ) : (
                <PublicInviteInactive
                  title="Acesso indisponível"
                  description="A área do cliente ainda não está disponível para este tipo de cadastro."
                />
              )}
              <div className="mx-auto mt-6 w-full max-w-xl space-y-3 text-center">
                <p className="text-sm text-muted-foreground">Ainda não participa do programa?</p>
                <Button variant="outline" size="lg" className="w-full" onClick={() => showGuest('cadastro')}>
                  <UserPlus className="size-4" />
                  Quero me cadastrar
                </Button>
                {showRules ? (
                  <button
                    type="button"
                    onClick={() => showGuest('informacoes')}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    <BookOpen className="size-4" />
                    Regras do programa e termos
                  </button>
                ) : null}
              </div>
            </>
          )}
        </TabPanel>
      </PublicInviteLayout>
    );
  }

  return (
    <PublicInviteLayout passbook={passbook} onHomeClick={() => void showMember('inicio')}>
      <div ref={topRef} className="scroll-mt-4" />
      <ParticipantSummary
        name={me.profile.name}
        firstName={me.firstName}
        backgroundColor={me.tier?.backgroundColor || passbook.backgroundColor}
        foregroundColor={me.tier?.foregroundColor || passbook.foregroundColor || passbook.labelColor}
        programName={passbook.title || passbook.organizationName}
        balance={memberView === 'inicio' ? undefined : formatBalance(me.balance, program)}
        tierLabel={me.tier?.label}
        tierColor={me.tier?.color || me.tier?.backgroundColor}
        onLogout={() => void onLogout()}
        loggingOut={loggingOut}
      />
      <RoundTabs
        items={memberTabs}
        active={memberView}
        onChange={(view) => void showMember(view)}
        ariaLabel="Minha conta"
        {...accent}
      />
      <TabPanel tabKey={memberView}>
        {memberView === 'dados' ? (
          <ProfileTab
            idPassbook={idPassbook}
            uniqueId={me.uniqueId}
            profile={me.profile}
            passbook={passbook}
            {...accent}
            onSaved={onProfileSaved}
            onDirtyChange={onProfileDirty}
          />
        ) : memberView === 'historico' ? (
          <HistoryTab idPassbook={idPassbook} passbook={program} />
        ) : memberView === 'cartao' ? (
          <CardTab
            passbook={passbook}
            serialNumber={me.serialNumber}
            holderName={me.profile.name}
            balance={formatBalance(me.balance, program)}
            tier={me.tier}
            {...accent}
          />
        ) : memberView === 'cupons' ? (
          <CouponsTab idPassbook={idPassbook} {...accent} />
        ) : memberView === 'indique' ? (
          <MgmTab idPassbook={idPassbook} />
        ) : memberView === 'pesquisa' && me.features.surveyId ? (
          <SurveyTab
            idPassbook={idPassbook}
            surveyId={me.features.surveyId}
            uniqueId={me.uniqueId}
            profile={me.profile}
            {...accent}
          />
        ) : memberView === 'regras' ? (
          <>
            <BackLink label="Voltar ao início" onClick={() => void showMember('inicio')} />
            <RulesAndTerms passbook={passbook} />
          </>
        ) : (
          <HomeTab
            idPassbook={idPassbook}
            passbook={passbook}
            me={me}
            program={program}
            {...accent}
            onNavigate={(to: HomeDestination) => void showMember(to)}
          />
        )}
      </TabPanel>
    </PublicInviteLayout>
  );
}
